/**
 * Parts tab: the specification sheet for the selected subsystem (Overview
 * with warranty and health badges, current part, lifecycle, early failure
 * alert, spare parts; Technical specs; Service history newest first;
 * Readings mapped to the subsystem's data items; Alternate parts from the
 * inventory) and the spare-parts inventory with in-memory reservations.
 */
import { useMemo, useState } from 'react';
import { CalendarDays, ClipboardCheck, Cog, Droplets, FileText, Hammer, MapPin, Package, Replace, Search, Sparkles, User, Wrench, type LucideIcon } from 'lucide-react';
import { Chip } from '@/components/ui/primitives';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/cn';
import { useUI } from '@/state/store';
import { findSubsystem, useTwin } from '../../store';
import { equipmentKeyFor, useInventoryView, type InventoryPart, type InventoryView } from '../adapters/inventory';
import { ageHealth, earlyFailure, findByName, findBySubsystem, flattenSpec, getSpec, matchSensorToItem, serviceTypeLabel, sortedServiceHistory, specSections, SPEC_SECTION_LABEL, useEquipmentSpecs, warrantyStatus, type EquipmentSpec } from '../adapters/specSheets';
import { SourceBadge, StatusIcon } from '../components/badges';
import { LinearGauge } from '../components/bars';
import { Sparkline } from '../components/Sparkline';
import { Button, Card, EmptyState, Row, SectionTitle, SubTabContent, SubTabs } from '../components/layout';
import { useAssetName } from '../hooks/useAssetName';
import { useItemLookup } from '../hooks/useItemLookup';
import { useNowMs } from '../hooks/useNowMs';
import { fmtAgo, fmtDate, fmtInt, fmtMoney, parseDateMs, titleCase } from '../lib/format';
import { stockBadge, stockStatus, stockStatusLabel, stockStatusTone } from '../lib/inventoryBadge';
import { parseNumeric } from '../lib/values';
import { useTwinUi } from '../state';
import { ReferenceSpecs } from '../components/ReferenceSpecs';
import { isModelOnlyElement } from '../../data/modelParts';

const SERVICE_ICON: Record<string, LucideIcon> = {
  INSTALL: Package,
  INSPECTION: Search,
  CLEANING: Sparkles,
  CALIBRATION: Cog,
  REPAIR: Hammer,
  REPLACE: Replace,
  LUBRICATION: Droplets,
  FIRMWARE: FileText,
};

export function PartsPanel() {
  const element = useTwin((s) => s.element);
  const selectedId = useUI((s) => s.selection.subsystemId);
  const partsInstanceId = useTwinUi((s) => s.partsInstanceId);
  const specs = useEquipmentSpecs();
  const inventory = useInventoryView();

  const subsystem = element && selectedId ? findSubsystem(element.subsystems, selectedId) : undefined;
  const spec = useMemo<EquipmentSpec | null>(() => {
    if (!specs.data) return null;
    const pinned = getSpec(specs.data, partsInstanceId);
    if (pinned) return pinned;
    if (!subsystem) return null;
    return findBySubsystem(specs.data, subsystem.id) ?? (!subsystem.modelOnly ? findByName(specs.data, subsystem.label) : null);
  }, [specs.data, partsInstanceId, subsystem]);

  if (!element) return <EmptyState title="No twin loaded." className="m-3" />;
  if (isModelOnlyElement(element)) return subsystem?.reference
    ? <ReferenceSpecs subsystem={subsystem} />
    : <EmptyState title="Select an instrument or spacecraft system for its published specifications." className="m-3">Select it in Health. No installed-part inventory or maintenance history is connected to this reference spacecraft.</EmptyState>;
  if (specs.isError || inventory.error) {
    return (
      <EmptyState title="Parts data did not load." className="m-3">
        {specs.error?.message ?? inventory.error?.message}
      </EmptyState>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-3 text-xs">
      {spec ? (
        <SpecSheet spec={spec} inventory={inventory} subsystemId={subsystem?.id ?? null} />
      ) : (
        <EmptyState title={subsystem ? `No specification sheet matches ${subsystem.label}.` : 'Select a subsystem to see its specification sheet.'}>
          {subsystem ? 'The lookup tried the subsystem id, its path and the keyword table. The inventory below is filtered by equipment keyword.' : 'The inventory below lists every spare part.'}
        </EmptyState>
      )}
      <InventoryList inventory={inventory} highlightEquipment={subsystem ? equipmentKeyFor(subsystem.id) : null} />
    </div>
  );
}

export function SpecSheet({ spec, inventory, subsystemId }: { spec: EquipmentSpec; inventory: InventoryView; subsystemId: string | null }) {
  const [tab, setTab] = useState('overview');
  const now = useNowMs(60_000);
  const warranty = warrantyStatus(spec, now);
  const health = ageHealth(spec, now);
  const early = earlyFailure(spec, now);
  const stock = inventory.partFor(spec.currentPart.partNumber);

  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-control border border-hairline bg-inset/60 px-3 py-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate font-display text-sm font-semibold text-ink">{spec.name}</div>
            <div className="flex items-center gap-1 text-[11px] text-ink-3">
              <MapPin className="h-3 w-3" /> {spec.location}
            </div>
          </div>
          <span className="font-mono text-[10px] text-ink-3">{spec.instanceId}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <Chip tone={warranty.tone} className="font-mono">
            Warranty {warranty.status}
          </Chip>
          <Chip tone={health.tone} className="font-mono">
            Health {health.percentage}%
          </Chip>
          {stock && <Chip tone={stockBadge(stock).tone}>{stockBadge(stock).label}</Chip>}
        </div>
      </div>
      <SubTabs
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'overview', label: 'Overview' },
          { key: 'specs', label: 'Specs' },
          { key: 'history', label: 'Service' },
          { key: 'readings', label: 'Readings' },
          { key: 'alternates', label: 'Alternates' },
        ]}
      >
        <SubTabContent value="overview">
          <div className="flex flex-col gap-2">
            <Card title="Current part">
              <Row label="Part number">
                <span className="font-mono">{spec.currentPart.partNumber}</span>
              </Row>
              <Row label="Serial number">
                <span className="font-mono">{spec.currentPart.serialNumber}</span>
              </Row>
              <Row label="Manufacturer">{spec.currentPart.manufacturer}</Row>
              <Row label="Model">{spec.currentPart.model}</Row>
            </Card>
            <Card title="Lifecycle">
              <Row label="Installed">{dateOrDash(spec.currentPart.installDate)}</Row>
              <Row label="Warranty expires">
                <span className={cn(warranty.tone === 'warning' && 'text-warning', warranty.tone === 'caution' && 'text-caution')}>{dateOrDash(spec.currentPart.warrantyExpires)}</span>
              </Row>
              <Row label="Expected end of life">{dateOrDash(spec.currentPart.expectedEndOfLife)}</Row>
              {spec.operationalData && (
                <>
                  <Row label="Operating hours">
                    <span className="font-mono">{fmtInt(spec.operationalData.totalOperatingHours)}</span>
                  </Row>
                  {spec.operationalData.powerCycles !== undefined && (
                    <Row label="Power cycles">
                      <span className="font-mono">{fmtInt(spec.operationalData.powerCycles)}</span>
                    </Row>
                  )}
                  {spec.operationalData.lastMaintenanceDate && (
                    <Row label="Last maintenance">
                      {dateOrDash(spec.operationalData.lastMaintenanceDate)}
                      {spec.operationalData.lastMaintenanceType ? `, ${spec.operationalData.lastMaintenanceType}` : ''}
                    </Row>
                  )}
                </>
              )}
              <div className="mt-1">
                <LinearGauge value={health.percentage} label="Age-based health" />
                <div className="text-[10px] text-ink-3">
                  {health.message}, {warranty.message}
                </div>
              </div>
            </Card>
            {early && (
              <div className="rounded-control border border-caution/40 bg-caution/10 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-caution">Early failure alert</div>
                <div className="text-ink">
                  Component may be failing <strong>{early.yearsEarly} years early</strong>.
                </div>
                <div className={early.warrantyClaimEligible ? 'text-nominal' : 'text-warning'}>{early.warrantyClaimEligible ? 'WARRANTY CLAIM ELIGIBLE' : 'Warranty has expired'}</div>
              </div>
            )}
            <Card title="Spare parts status">
              {stock ? (
                <div className="flex items-start gap-2">
                  <Package className="mt-0.5 h-4 w-4 shrink-0 text-ink-3" />
                  <div>
                    <div className="text-ink">{stock.quantity > 0 ? `${stock.quantity} in stock at ${stock.location}` : `Out of stock, ${stock.leadTimeDays} day lead time`}</div>
                    <div className="text-[11px] text-ink-3">
                      {stock.name}, {fmtMoney(stock.cost)}, minimum {stock.minStock}
                    </div>
                  </div>
                </div>
              ) : (
                <span className="text-ink-3">Inventory data unavailable for {spec.currentPart.partNumber}.</span>
              )}
            </Card>
          </div>
        </SubTabContent>
        <SubTabContent value="specs">
          <div className="flex flex-col gap-2">
            {specSections(spec).map((section) => (
              <Card key={section} title={SPEC_SECTION_LABEL[section] ?? titleCase(section)}>
                <table className="w-full">
                  <tbody className="divide-y divide-hairline">
                    {flattenSpec(spec.technicalSpecs?.[section]).map(([k, v]) => (
                      <tr key={k}>
                        <td className="py-1 pr-2 align-top text-ink-3">{k}</td>
                        <td className="py-1 text-right font-mono text-ink">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            ))}
            {specSections(spec).length === 0 && <EmptyState title="No technical specifications on this sheet." />}
            {spec.documents && spec.documents.length > 0 && (
              <Card title="Documents">
                <ul className="flex flex-col gap-0.5">
                  {spec.documents.map((d) => (
                    <li key={d.url} className="flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5 text-ink-3" />
                      <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-ink-2 underline decoration-hairline-strong hover:text-accent">
                        {d.name}
                      </a>
                      <span className="text-[10px] text-ink-3">({d.type}; not served in this build)</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
        </SubTabContent>
        <SubTabContent value="history">
          <ServiceHistory spec={spec} />
        </SubTabContent>
        <SubTabContent value="readings">
          <SpecReadings spec={spec} subsystemId={subsystemId} />
        </SubTabContent>
        <SubTabContent value="alternates">
          <AlternateParts spec={spec} inventory={inventory} />
        </SubTabContent>
      </SubTabs>
    </div>
  );
}

function dateOrDash(s: string | undefined): string {
  const ms = parseDateMs(s);
  return ms === null ? '--' : fmtDate(ms);
}

function ServiceHistory({ spec }: { spec: EquipmentSpec }) {
  const entries = useMemo(() => sortedServiceHistory(spec), [spec]);
  if (entries.length === 0) return <EmptyState title="No service history available." />;
  return (
    <ol className="flex flex-col gap-1.5">
      {entries.map((e, i) => {
        const Icon = SERVICE_ICON[e.type.toUpperCase()] ?? ClipboardCheck;
        const ms = parseDateMs(e.date);
        return (
          <li key={`${e.date}-${i}`} className="flex gap-2 rounded-control border border-hairline bg-raised px-3 py-2">
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ink-3" aria-label={serviceTypeLabel(e.type)} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-mono text-[11px] text-ink-3">{ms === null ? e.date : fmtDate(ms)}</span>
                <Chip tone="neutral" className="px-1.5 py-0 text-[9px]">
                  {serviceTypeLabel(e.type)}
                </Chip>
              </div>
              <div className="text-ink">{e.description}</div>
              {(e.partNumber || e.serialNumber) && (
                <div className="font-mono text-[10px] text-ink-3">
                  {e.partNumber && `Part ${e.partNumber}`}
                  {e.partNumber && e.serialNumber && ', '}
                  {e.serialNumber && `S/N ${e.serialNumber}`}
                </div>
              )}
              {e.results && (
                <div className="mt-0.5 flex flex-wrap gap-1">
                  {Object.entries(e.results).map(([k, v]) => (
                    <span key={k} className="rounded-control border border-hairline px-1.5 py-0.5 text-[10px] text-ink-2">
                      {titleCase(k)}: {String(v)}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-0.5 flex flex-wrap gap-2 text-[10px] text-ink-3">
                {e.technician && (
                  <span className="inline-flex items-center gap-1">
                    <User className="h-3 w-3" /> {e.technician}
                  </span>
                )}
                {e.workOrder && (
                  <span className="inline-flex items-center gap-1 font-mono">
                    <ClipboardCheck className="h-3 w-3" /> {e.workOrder}
                  </span>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function trendOf(values: number[]): 'rising' | 'falling' | 'stable' {
  if (values.length < 3) return 'stable';
  const recent = values.slice(-3).reduce((a, b) => a + b, 0) / 3;
  const older = values.slice(0, 3).reduce((a, b) => a + b, 0) / 3;
  if (older === 0) return 'stable';
  const change = ((recent - older) / Math.abs(older)) * 100;
  return change > 5 ? 'rising' : change < -5 ? 'falling' : 'stable';
}

function SpecReadings({ spec, subsystemId }: { spec: EquipmentSpec; subsystemId: string | null }) {
  const element = useTwin((s) => s.element)!;
  const history = useTwin((s) => s.history);
  const lookup = useItemLookup();
  const now = useNowMs();
  const subsystem = subsystemId ? findSubsystem(element.subsystems, subsystemId) : undefined;
  const itemIds = subsystem?.data.map((d) => d.id) ?? [];
  const sensorIds = spec.sensorIds ?? [];
  if (sensorIds.length === 0) return <EmptyState title="No sensor ids configured for this equipment." />;
  if (!subsystem) return <EmptyState title="Select the subsystem in the Health tab to map its readings onto this sheet." />;
  return (
    <div className="grid grid-cols-1 gap-1.5">
      {sensorIds.map((sensorId) => {
        const itemId = matchSensorToItem(sensorId, itemIds);
        const item = itemId ? subsystem.data.find((d) => d.id === itemId) : undefined;
        if (!item) {
          return (
            <Card key={sensorId} title={titleCase(sensorId)}>
              <span className="text-ink-3">No reading mapped on {subsystem.label}.</span>
            </Card>
          );
        }
        const v = lookup.view(subsystem.id, item);
        const stored = history[`${subsystem.id}.${item.id}`] ?? [];
        const values =
          stored.length > 1
            ? stored.map((r) => r.numericValue).filter((n): n is number => n !== null)
            : (item.history ?? [])
                .map((h) => parseNumeric(h.value))
                .filter((n): n is number => n !== null)
                .reverse();
        const trend = trendOf(values.slice(-10));
        return (
          <Card key={sensorId} title={item.label} right={<SourceBadge source={v.source} />}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <StatusIcon colour={v.colour} />
                <span className="font-mono text-base text-ink">{v.value}</span>
              </div>
              <Sparkline values={values.slice(-30)} band={item.normalRange ? { low: item.normalRange.low, high: item.normalRange.high } : null} />
            </div>
            <div className="mt-1 flex items-center justify-between text-[10px] text-ink-3">
              <span>{item.normalRange ? `Normal ${item.normalRange.low} to ${item.normalRange.high}${item.normalRange.unit ?? ''}` : 'Range not configured'}</span>
              <span className={cn(trend !== 'stable' && 'text-caution')}>{trend}</span>
            </div>
            <div className="text-[10px] text-ink-3">{v.timeMs !== null ? `Last updated ${fmtAgo(now - v.timeMs)}` : 'Static value from the element JSON'}</div>
          </Card>
        );
      })}
    </div>
  );
}

function AlternateParts({ spec, inventory }: { spec: EquipmentSpec; inventory: InventoryView }) {
  const current = inventory.partFor(spec.currentPart.partNumber);
  const alternates = spec.alternatePartNumbers ?? [];
  if (alternates.length === 0) return <EmptyState title="No alternate parts defined for this equipment." />;
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[11px] text-ink-3">Compatible replacement parts from different manufacturers.</p>
      <Card title="Currently installed" className="border-accent/40">
        <div className="text-ink">{spec.currentPart.manufacturer}</div>
        <div className="text-[11px] text-ink-2">{spec.currentPart.model}</div>
        <Row label="Part number">
          <span className="font-mono">{spec.currentPart.partNumber}</span>
        </Row>
        {current && (
          <>
            <Row label="Cost">{fmtMoney(current.cost)}</Row>
            <Row label="Lead time">{current.leadTimeDays} days</Row>
            <Row label="In stock">{current.quantity}</Row>
          </>
        )}
      </Card>
      {alternates.map((pn) => {
        const alt = inventory.partFor(pn);
        return (
          <Card key={pn} title={alt?.vendor ?? 'Unknown vendor'}>
            {alt ? (
              <>
                <div className="text-ink">{alt.name}</div>
                <Row label="Part number">
                  <span className="font-mono">{pn}</span>
                </Row>
                <Row label="Cost">{fmtMoney(alt.cost)}</Row>
                <Row label="Lead time">{alt.leadTimeDays} days</Row>
                <Row label="In stock">
                  <span className="inline-flex items-center gap-1.5">
                    {alt.quantity} <Chip tone={stockBadge(alt).tone}>{stockBadge(alt).label}</Chip>
                  </span>
                </Row>
                {alt.description && <p className="mt-1 text-[11px] text-ink-3">{alt.description}</p>}
              </>
            ) : (
              <>
                <div className="font-mono text-ink">{pn}</div>
                <div className="text-ink-3">Part not found in inventory.</div>
              </>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function InventoryList({ inventory, highlightEquipment }: { inventory: InventoryView; highlightEquipment: string | null }) {
  const name = useAssetName();
  const [showAll, setShowAll] = useState(false);
  if (!inventory.data) return <div className="text-ink-3">Loading inventory.</div>;
  const shown = inventory.parts.filter((p) => showAll || !highlightEquipment || p.equipment === highlightEquipment);
  const onReserve = (part: InventoryPart) => {
    const r = inventory.reserve(part.partNumber, `Reserved from the ${name?.title ?? 'twin'} Parts tab`);
    if (r) toast('success', `${part.partNumber} reserved (${r.id}), in memory only.`);
    else toast('warn', `${part.partNumber} has no free units to reserve.`);
  };
  return (
    <div>
      <SectionTitle
        right={
          highlightEquipment && (
            <button type="button" onClick={() => setShowAll((v) => !v)} className="text-[10px] text-ink-3 underline decoration-hairline-strong hover:text-ink">
              {showAll ? `Only ${highlightEquipment.replace(/_/g, ' ')}` : `All ${inventory.parts.length} parts`}
            </button>
          )
        }
      >
        <span className="inline-flex items-center gap-1">
          <Package className="h-3 w-3" /> Inventory
        </span>
      </SectionTitle>
      <ul className="flex flex-col gap-1">
        {shown.map((p) => {
          const badge = stockBadge(p);
          const status = stockStatus(p);
          const highlighted = highlightEquipment === p.equipment;
          const mine = inventory.reservations.filter((r) => r.partNumber === p.partNumber);
          return (
            <li key={p.partNumber} className={cn('rounded-control border px-3 py-2', highlighted ? 'border-hairline-strong bg-raised' : 'border-hairline bg-raised/60')}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-ink">{p.name}</div>
                  <div className="font-mono text-[10px] text-ink-3">
                    {p.partNumber}
                    {p.vendor ? `, ${p.vendor}` : ''}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <Chip tone={badge.tone}>{badge.label}</Chip>
                  <Chip tone={stockStatusTone(status)} className="px-1.5 py-0 text-[9px]">
                    {stockStatusLabel(status)}
                  </Chip>
                </div>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-ink-3">
                <span className="font-mono text-ink-2">qty {p.quantity}</span>
                <span>min {p.minStock}</span>
                <span>lead {p.leadTimeDays} d</span>
                <span>{fmtMoney(p.cost)}</span>
                <span className="inline-flex items-center gap-0.5">
                  <MapPin className="h-3 w-3" /> {p.location}
                  {p.alternateLocations && p.alternateLocations.length > 0 && `, also ${p.alternateLocations.join(', ')}`}
                </span>
                {p.onOrder && p.expectedDelivery && (
                  <span className="inline-flex items-center gap-0.5 text-live">
                    <CalendarDays className="h-3 w-3" /> on order, due {dateOrDash(p.expectedDelivery)}
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1">
                <Button onClick={() => onReserve(p)} disabled={p.quantity === 0 || !inventory.manager} className="px-2 py-1" title="Reserve one unit (in memory)">
                  <Wrench className="h-3 w-3" /> Reserve
                </Button>
                {mine.map((r) => (
                  <button key={r.id} type="button" onClick={() => inventory.release(r.id, false)} className="rounded-control border border-dashed border-hairline-strong px-1.5 py-0.5 font-mono text-[10px] text-ink-3 hover:text-ink" title="Release this reservation back to stock">
                    {r.id} release
                  </button>
                ))}
              </div>
            </li>
          );
        })}
        {shown.length === 0 && <EmptyState title="No parts for this equipment keyword." />}
      </ul>
      {inventory.reservations.length > 0 && (
        <p className="mt-1 text-[10px] text-ink-3">
          {inventory.reservations.length} active reservation{inventory.reservations.length === 1 ? '' : 's'}, in memory only; they clear on reload.
        </p>
      )}
    </div>
  );
}
